(() => {
  if (window.KlaimCartShipping) return;
  const retiredCode = 'KLAIM230-YEEZY-8F4K2';
  const internalCodes = ['KLAIMSETS2', 'KLAIMSETS3'];
  const normalizeCode = code => String(code || '').trim().toUpperCase();
  const shopRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let cleanupPromise = null;
  let notice = '';
  const resumedForms = new WeakSet();

  async function requestCart(url, body) {
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Your bag could not be updated. Please try again.');
    return cart;
  }

  function showNotice(message) {
    notice = message;
    document.querySelectorAll('[data-klaim-retired-gift-status]').forEach(element => {
      element.textContent = message;
      element.hidden = !message;
    });
  }

  function clearRetiredSession() {
    try {
      ['klaim-cart-discount-code', 'klaim-cart-discount-applied', 'klaim-cart-discount-pending'].forEach(key => {
        if (normalizeCode(sessionStorage.getItem(key)) === retiredCode) sessionStorage.removeItem(key);
      });
      ['klaim-cart-reward-choice', 'klaim-free-yeezy-prompt-dismissed', 'klaim-suppress-gift-auto-open-once'].forEach(key => sessionStorage.removeItem(key));
    } catch (_) {
      // Cart cleanup still works when browser storage is unavailable.
    }
  }

  // The campaign marker distinguishes expired gifts from ordinary paid Slides.
  function cleanRetiredGift() {
    if (cleanupPromise) return cleanupPromise;
    clearRetiredSession();
    cleanupPromise = (async () => {
      const cart = await requestCart(`${shopRoot}cart.js`);
      const gifts = (cart.items || []).filter(item => item.properties?._klaim_free_yeezy === 'true');
      const codes = cart.discount_codes || [];
      const hasRetiredCode = codes.some(item => normalizeCode(item.code) === retiredCode);
      if (!gifts.length && !hasRetiredCode) return cart;
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map(section => section.id) || [];
      const body = { sections, sections_url: window.location.pathname };
      if (gifts.length) body.updates = Object.fromEntries(gifts.map(item => [item.key, 0]));
      if (hasRetiredCode) body.discount = codes.filter(item => normalizeCode(item.code) !== retiredCode).map(item => item.code).join(',');
      const updated = await requestCart(window.routes?.cart_update_url || `${shopRoot}cart/update.js`, body);
      if ((updated.items || []).some(item => item.properties?._klaim_free_yeezy === 'true')) {
        throw new Error('The expired gift could not be removed. Please remove it from your bag before checkout.');
      }
      if (gifts.length) notice = 'The free Slides offer has ended. Its gift was removed from your bag.';
      // Notify the old cart elements before rendering; new subscribers must not replace the drawer with cart-page markup.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'klaim-retired-gift', cartData: updated });
      }
      if (drawer && updated.sections) drawer.renderContents(updated);
      showNotice(notice);
      updated.retiredGiftRemoved = gifts.length > 0;
      return updated;
    })().finally(() => { cleanupPromise = null; });
    return cleanupPromise;
  }

  // Cleanup before submission prevents an old gift from becoming a paid item.
  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout') return;
    if (resumedForms.has(form)) {
      resumedForms.delete(form);
      return;
    }
    event.preventDefault();
    if (form.dataset.klaimCleaning === 'true') return;
    form.dataset.klaimCleaning = 'true';
    try {
      const cart = await cleanRetiredGift();
      if (cart.retiredGiftRemoved && form.id !== 'CartDrawer-Form') {
        window.location.reload();
        return;
      }
      const currentForm = form.isConnected ? form : document.getElementById(form.id);
      const currentButton = button?.isConnected ? button : document.getElementById(button?.id);
      if (currentForm && currentButton && !currentButton.disabled) {
        resumedForms.add(currentForm);
        currentForm.requestSubmit(currentButton);
      }
    } catch (error) {
      showNotice(error.message);
      const errorElement = document.querySelector('#cart-errors');
      if (errorElement) errorElement.textContent = error.message;
    } finally {
      delete form.dataset.klaimCleaning;
    }
  }, true);

  class KlaimCartShipping extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      showNotice(notice);
      cleanRetiredGift().then(cart => {
        if (this.isConnected) this.syncMysteryFreeShippingUi(cart);
      }).catch(error => showNotice(error.message));
    }

    syncMysteryFreeShippingUi(cart) {
      if (!(cart.discount_codes || []).some(item => item.applicable !== false && normalizeCode(item.code).startsWith('SHIPFREEEU-'))) return;
      this.classList.add('has-free-shipping', 'has-mystery-free-shipping');
      const milestone = this.querySelector('.klaim-cart-shipping__milestone--shipping');
      milestone?.classList.add('is-complete');
      const detail = milestone?.querySelector('.klaim-cart-shipping__milestone-detail');
      if (detail) detail.textContent = 'UNLOCKED WITH MYSTERY GIFT';
      this.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', '100');
      const fill = this.querySelector('.klaim-cart-shipping__fill');
      if (fill) fill.style.width = '100%';
    }

    getApplicableCodes(cart) {
      return (cart.discount_codes || []).filter(item => item.applicable && normalizeCode(item.code) !== retiredCode).map(item => item.code);
    }

    getInternalCodes(cart) {
      if (window.KlaimBundleDiscounts) return window.KlaimBundleDiscounts.getDesiredCodes(cart);
      const codes = this.getApplicableCodes(cart).filter(code => internalCodes.includes(normalizeCode(code)));
      const quantity = (cart.items || []).reduce((total, item) => item.properties?._klaim_essentials_set === 'true' ? total + item.quantity : total, 0);
      const desired = quantity >= 3 ? 'KLAIMSETS3' : quantity >= 2 ? 'KLAIMSETS2' : '';
      if (desired && !codes.includes(desired)) codes.push(desired);
      return codes;
    }

    isCodeApplicable(cart, code) {
      return (cart.discount_codes || []).some(item => item.applicable && normalizeCode(item.code) === normalizeCode(code));
    }

    getSections() {
      return this.closest('cart-drawer')?.getSectionsToRender().map(section => section.id) || [];
    }

    updateDiscountCodes(codes, sections = []) {
      const unique = [...new Set(codes.map(code => String(code || '').trim()).filter(code => code && normalizeCode(code) !== retiredCode))];
      return requestCart(window.routes?.cart_update_url || `${shopRoot}cart/update.js`, {
        discount: unique.join(','), sections, sections_url: window.location.pathname
      });
    }

    async syncDiscountsWithSections() {
      const sections = this.getSections();
      if (window.KlaimEssentialsSetDiscount) {
        const cart = await window.KlaimEssentialsSetDiscount.sync({ sections });
        if (cart.sections) return cart;
      }
      const cart = await requestCart(`${shopRoot}cart.js`);
      return this.updateDiscountCodes(this.getApplicableCodes(cart), sections);
    }

    async handleCustomerDiscount(code) {
      if (normalizeCode(code) === retiredCode) throw new Error('This promotion has ended.');
      if (this.busy) throw new Error('Your discount is still being applied.');
      this.busy = true;
      try {
        const cart = await cleanRetiredGift();
        const originalCodes = this.getApplicableCodes(cart);
        let state = await this.updateDiscountCodes([...this.getInternalCodes(cart), code]);
        if (!this.isCodeApplicable(state, code)) {
          await this.updateDiscountCodes(originalCodes);
          throw new Error('This discount code is invalid or cannot be combined with your current bag.');
        }
        if (window.KlaimEssentialsSetDiscount) {
          state = await window.KlaimEssentialsSetDiscount.sync();
          if (!this.isCodeApplicable(state, code)) {
            await this.updateDiscountCodes(originalCodes);
            throw new Error('This discount code cannot be combined with your current bag.');
          }
        }
        sessionStorage.setItem('klaim-cart-discount-code', code);
        sessionStorage.setItem('klaim-cart-discount-applied', code);
        sessionStorage.removeItem('klaim-cart-discount-pending');
        state = await this.syncDiscountsWithSections();
        if (!this.isCodeApplicable(state, code)) throw new Error('Your discount could not be confirmed. Please try again.');
        const drawer = this.closest('cart-drawer');
        if (drawer && state.sections) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'klaim-cart-discount', cartData: state });
          }
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }
      } finally { this.busy = false; }
    }
  }

  if (!customElements.get('klaim-cart-rewards')) customElements.define('klaim-cart-rewards', KlaimCartShipping);
  clearRetiredSession();
  window.KlaimCartShipping = { version: 'shipping-only-v1', cleanRetiredGift };
  if (!document.querySelector('klaim-cart-rewards')) cleanRetiredGift().catch(error => showNotice(error.message));
})();
